import { useCallback } from 'react'
import { useApolloClient } from '@apollo/client'
import type { DocumentNode, ApolloClient } from '@apollo/client'
import { useUnmountableState } from '@fs/zion-frontend-friends'
import { brightspotConfig } from '../../config'

const { fetchPolicy: fetchPolicyOptions } = brightspotConfig.query
type FetchPolicy = (typeof fetchPolicyOptions)[keyof typeof fetchPolicyOptions]

interface PaginationData {
  pageCount: number
}

interface PageResult<T> {
  paginationData: PaginationData
  results: T[]
}

interface FetchPageParams {
  client: ApolloClient<unknown>
  query: DocumentNode
  variables: Record<string, unknown>
  dataKey: string
  page: number
  pageLimit: number
  fetchPolicy: FetchPolicy
}

async function fetchPage<T>({
  client,
  query,
  variables,
  dataKey,
  page,
  pageLimit,
  fetchPolicy,
}: FetchPageParams): Promise<PageResult<T>> {
  const resp = await client.query<Record<string, PageResult<T>>>({
    query,
    variables: { ...variables, pageRequest: { page, limit: pageLimit } },
    fetchPolicy,
  })
  return resp.data[dataKey]
}

interface UseFetchAllPagesOptions {
  dataKey: string
  pageLimit?: number
  fetchPolicy?: FetchPolicy
  client?: ApolloClient<unknown>
}

export default function useFetchAllPages<T = unknown>(
  query: DocumentNode,
  {
    dataKey,
    pageLimit = brightspotConfig.query.defaults.pageRequest.limit,
    fetchPolicy = fetchPolicyOptions.NETWORK_ONLY,
    client: clientProp,
  }: UseFetchAllPagesOptions
): {
  fetchAll: (variables?: Record<string, unknown>) => Promise<T[]>
  data: T[] | undefined
  loading: boolean
  error: Error | undefined
} {
  const defaultClient = useApolloClient()
  const client = clientProp ?? defaultClient

  const [data, setData] = useUnmountableState<T[] | undefined>(undefined)
  const [loading, setLoading] = useUnmountableState(false)
  const [error, setError] = useUnmountableState<Error | undefined>(undefined)

  const fetchAll = useCallback(
    async (variables: Record<string, unknown> = {}) => {
      setLoading(true)
      setError(undefined)

      const params = { client, query, variables, dataKey, pageLimit, fetchPolicy }

      try {
        const firstPage = await fetchPage<T>({ ...params, page: 0 })
        const { pageCount } = firstPage.paginationData

        const remainingRequests: Promise<PageResult<T>>[] = []
        for (let page = 1; page < pageCount; page++) {
          remainingRequests.push(fetchPage<T>({ ...params, page }))
        }

        const remainingPages = await Promise.all(remainingRequests)
        const results = [firstPage, ...remainingPages].flatMap((p) => p.results)

        setData(results)
        setLoading(false)
        return results
      } catch (err) {
        setError(err as Error)
        setLoading(false)
        return []
      }
    },
    [client, dataKey, fetchPolicy, pageLimit, query, setData, setError, setLoading]
  )

  return { fetchAll, data, loading, error }
}
